/**
 * Shared admissions apply form (issue #17) — extracted verbatim from `ApplyPage`
 * so BOTH the public landing REQUEST section and the `/apply` route drive the same
 * unauthenticated `POST /public/apply` flow.
 *
 * Layout-neutral: no `AuthCard`, no page chrome — the caller wraps it. It owns all
 * field state, its own `getPublicPrograms()` query, the submit, and the success
 * confirmation. Sends no `Authorization`/`X-Tenant` (via `lib/api/publicApply.ts`):
 * the tenant is resolved server-side from the trusted Origin, and no account is
 * created — "no account, no payment" is the real product behavior.
 */
import { useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { PUBLIC_FOCUS, PUBLIC_LINK } from "@/components/public/control-classes";
import { ArrowRight, Check, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TurnstileField } from "@/components/apply/TurnstileField";
import { turnstileEnabled } from "@/components/apply/turnstile";
import {
  GUARDIAN_SECTION_HEADING,
  INTAKE_FIELD_PRESENTATION,
  PARTICIPANT_SECTION_HEADING,
  type IntakeFieldGroup,
} from "@/components/apply/intake-fields";
import { LEGAL_ROUTES } from "@/content/legal";
import { ApiError } from "@/lib/api/publicClient";
import {
  getPublicIntakeFields,
  getPublicPrograms,
  submitApplication,
  type ApplyRequest,
  type IntakeField,
  type IntakeFieldKey,
} from "@/lib/api/publicApply";

const UNDECIDED = "__undecided__";

interface ApplyFormProps {
  applicantRoleLabel?: string;
  confirmationUnavailableLead?: ReactNode;
  contactEmail?: string | null;
  editorialSections?: boolean;
  intakeFieldsFn?: typeof getPublicIntakeFields;
  notePlaceholder?: string;
  phoneHint?: string | null;
  phoneRequired?: boolean;
  showBackToSignIn?: boolean;
  showProgram?: boolean;
  submitApplicationFn?: typeof submitApplication;
  submitLabel?: string;
  submissionNoun?: "application" | "request";
  successHeading?: string;
  successLead?: ReactNode;
}

export function ApplyForm({
  applicantRoleLabel = "I am the",
  confirmationUnavailableLead,
  contactEmail = null,
  editorialSections = false,
  intakeFieldsFn = getPublicIntakeFields,
  notePlaceholder = "Prior study, the student's age, scheduling, which location…",
  phoneHint = "optional, WhatsApp ok",
  phoneRequired = false,
  showBackToSignIn = false,
  showProgram = true,
  submitApplicationFn = submitApplication,
  submitLabel = "Request a seat",
  submissionNoun = "request",
  successHeading = "Request received.",
  successLead = (
    <>
      A director will review your request and reach out within a few days, usually by email, with
      next steps.
    </>
  ),
}: ApplyFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailMismatch, setEmailMismatch] = useState(false);
  const [phone, setPhone] = useState("");
  const [programId, setProgramId] = useState(UNDECIDED);
  const [applicantIs, setApplicantIs] = useState<ApplyRequest["applicant_is"] | "">("");
  const [details, setDetails] = useState<Partial<Record<IntakeFieldKey, string>>>({});
  const [guardianConsent, setGuardianConsent] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [confirmationEmailSent, setConfirmationEmailSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileCycle, setTurnstileCycle] = useState(0);
  const submissionId = useRef<string | null>(null);

  const changeField = <T,>(setter: (value: T) => void, value: T) => {
    submissionId.current = null;
    setter(value);
  };

  const changeEmailField = (setter: (value: string) => void, value: string) => {
    setEmailMismatch(false);
    changeField(setter, value);
  };

  const programs = useQuery({
    queryKey: ["public-programs"],
    queryFn: getPublicPrograms,
    enabled: showProgram,
  });

  // Which detail fields this tenant collects. Neither a failed read nor a slow
  // one may degrade into a shorter form: the database enforces the same
  // configuration, so the applicant would fill in what is shown, submit, and be
  // refused for fields the form never displayed. While the read is in flight
  // this component renders identically to a tenant that collects nothing, which
  // is exactly why the pending state needs its own hold on submission.
  const intake = useQuery({
    queryKey: ["public-intake-fields"],
    queryFn: intakeFieldsFn,
  });
  const intakeUnsettled = intake.isPending || intake.isError;
  const intakeFields: IntakeField[] = intake.data ?? [];
  const fieldsInGroup = (group: IntakeFieldGroup) =>
    intakeFields.filter((field) => INTAKE_FIELD_PRESENTATION[field.key].group === group);
  const participantFields = fieldsInGroup("participant");
  const guardianFields = fieldsInGroup("guardian");

  const changeDetail = (key: IntakeFieldKey, value: string) => {
    submissionId.current = null;
    setDetails((current) => ({ ...current, [key]: value }));
  };

  const detailPayload = (): Partial<Record<IntakeFieldKey, string>> => {
    const payload: Partial<Record<IntakeFieldKey, string>> = {};
    for (const field of intakeFields) {
      const value = (details[field.key] ?? "").trim();
      if (value) {
        payload[field.key] = value;
      }
    }
    return payload;
  };

  const missingRequiredDetail = intakeFields.some(
    (field) => field.required && !(details[field.key] ?? "").trim(),
  );

  /**
   * Whether the submitted date of birth puts the participant under 13 (D7).
   *
   * A birthday exactly 13 years ago today is 13, not under it, so the
   * comparison is strict. The same rule is enforced in `lms_apply_v3`, and the
   * database is the authority: this only decides whether the form ASKS, so a
   * clock skew between browser and server costs a server-side refusal with a
   * clear message rather than an application stored without consent.
   */
  const isUnderThirteen = (() => {
    const raw = (details.date_of_birth ?? "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false;
    const dob = new Date(`${raw}T00:00:00`);
    if (Number.isNaN(dob.getTime())) return false;
    const thirteenthBirthday = new Date(dob);
    thirteenthBirthday.setFullYear(thirteenthBirthday.getFullYear() + 13);
    return thirteenthBirthday > new Date();
  })();

  // A named guardian is what the consent attaches to, so consent without one
  // is refused by the database. Mirror that here rather than let the applicant
  // tick a box that cannot be honoured.
  const guardianNamed =
    Boolean((details.mother_name ?? "").trim()) || Boolean((details.father_name ?? "").trim());
  const consentOutstanding = isUnderThirteen && (!guardianNamed || !guardianConsent);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const normalizedEmail = trimmedEmail.toLowerCase();
    const normalizedConfirmation = confirmEmail.trim().toLowerCase();
    // Belt-and-suspenders with the native `required`/`type="email"` validation
    // below (the form no longer opts out via `noValidate`): `applicantIs` is a
    // Radix Select, not a native form control, so it needs its own guard
    // regardless; name/email are re-checked here too since a non-browser
    // client could still call this handler directly.
    if (
      !applicantIs ||
      !trimmedName ||
      !trimmedEmail ||
      !trimmedEmail.includes("@") ||
      (phoneRequired && !trimmedPhone)
    ) {
      return;
    }
    if (normalizedEmail !== normalizedConfirmation) {
      setEmailMismatch(true);
      return;
    }
    if (intakeUnsettled || missingRequiredDetail || consentOutstanding) {
      return;
    }
    if (turnstileEnabled && !turnstileToken) {
      setSubmitError(`Complete the security verification before sending your ${submissionNoun}.`);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    submissionId.current ??= crypto.randomUUID();
    try {
      const result = await submitApplicationFn({
        name: trimmedName,
        email: trimmedEmail,
        phone: trimmedPhone || null,
        program_id: programId === UNDECIDED ? null : programId,
        applicant_is: applicantIs,
        applicant_message: note.trim() || null,
        submission_id: submissionId.current,
        turnstile_token: turnstileToken,
        ...detailPayload(),
        ...(isUnderThirteen ? { guardian_consent: guardianConsent } : {}),
      });
      setSubmittedEmail(trimmedEmail);
      setConfirmationEmailSent(result.confirmation_email_sent);
      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof ApiError && error.status === 429
          ? `Too many ${submissionNoun} attempts were received for this email. Wait an hour, then try again.`
          : `We couldn’t send your ${submissionNoun}. Try again.`,
      );
      setTurnstileToken(null);
      setTurnstileCycle((current) => current + 1);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="grid justify-items-center gap-4 text-center" role="status" aria-live="polite">
        <div
          className="grid size-14 place-items-center rounded-full"
          style={{ border: "1px solid color-mix(in oklab, var(--public-highlight-deep) 60%, transparent)" }}
        >
          <Check className="size-6 text-[var(--public-readable-highlight)]" strokeWidth={1.8} aria-hidden="true" />
        </div>
        <h3 className="font-display text-2xl font-semibold text-[var(--public-foreground)]">{successHeading}</h3>
        <p className="text-ui text-[var(--public-foreground-soft)]">
          {successLead}{" "}
          {confirmationEmailSent ? (
            <>We sent a confirmation to <strong>{submittedEmail}</strong>. </>
          ) : (
            confirmationUnavailableLead ?? (
              <>Your {submissionNoun} is recorded even if the confirmation email is delayed. </>
            )
          )}
          {contactEmail && (
            <>
              If it&rsquo;s time-sensitive, write to{" "}
              <a
                href={`mailto:${contactEmail}`}
                className={`${PUBLIC_LINK} text-[var(--public-primary)] hover:underline`}
              >
                {contactEmail}
              </a>{" "}
              directly.
            </>
          )}
        </p>
        {showBackToSignIn && (
          <Link
            to="/sign-in"
            className={`${PUBLIC_LINK} text-ui text-[var(--public-primary)] hover:underline`}
          >
            Back to sign in
          </Link>
        )}
      </div>
    );
  }

  // A date of birth in the future is refused by the database. Bounding the
  // picker says so before the applicant submits rather than after.
  const today = new Date().toISOString().slice(0, 10);

  // Editorial sections are labelled, not numbered. The landing page they sit on
  // spends 01/02/03 on the three stages of the rhythm; a second and third
  // numbered sequence in the same scroll stops reading as order.
  const renderSectionHeading = (
    label: string,
    id: string,
    editorialGridClass = "",
  ) =>
    editorialSections ? (
      <div
        className={`flex items-center gap-4 md:col-span-2 ${editorialGridClass}`}
        data-apply-section-heading
      >
        <h3 id={id} className="shrink-0">
          <span data-apply-section-label>{label}</span>
        </h3>
        <hr aria-hidden="true" data-apply-section-rule />
      </div>
    ) : (
      <h3 id={id} className="eyebrow" data-apply-section-heading>
        {label}
      </h3>
    );

  const renderDetailFields = (group: IntakeFieldGroup) =>
    fieldsInGroup(group)
      .map((field) => {
        const presentation = INTAKE_FIELD_PRESENTATION[field.key];
        const inputId = `ap-${field.key.replaceAll("_", "-")}`;
        const optional = !field.required;
        const editorialGridClass = editorialSections
          ? presentation.wide
            ? group === "participant"
              ? "md:col-span-2 lg:col-span-3"
              : "md:col-span-2"
            : ""
          : "";
        return (
          // The data attributes are the layout seam: a caller that arranges
          // this form in columns (the landing page does) needs to know which
          // field is which and which ones want a full row, without importing
          // the field list or reaching in by input id.
          <div
            key={field.key}
            className={`grid gap-2 ${editorialGridClass}`}
            data-apply-field={field.key}
            data-apply-wide={presentation.wide ? "" : undefined}
          >
            <Label
              htmlFor={inputId}
              data-apply-qualified={
                optional || presentation.hint || presentation.helper ? "" : undefined
              }
            >
              {presentation.label}
              {optional && (
                <span className="text-muted-foreground" data-apply-optional>
                  {" "}(optional)
                </span>
              )}
              {presentation.hint && (
                <span className="text-muted-foreground" data-apply-helper>
                  ({presentation.hint})
                </span>
              )}
              {presentation.helper && (
                <span className="text-muted-foreground" data-apply-helper>
                  {presentation.helper}
                </span>
              )}
            </Label>
            <Input
              id={inputId}
              type={presentation.type}
              autoComplete={presentation.autoComplete}
              placeholder={presentation.placeholder}
              max={presentation.type === "date" ? today : undefined}
              value={details[field.key] ?? ""}
              onChange={(e) => changeDetail(field.key, e.target.value)}
              maxLength={presentation.maxLength}
              required={field.required}
            />
          </div>
        );
      });

  const contactFields = (
    <>
      <div
        className={`grid gap-2 ${editorialSections ? "lg:col-span-2" : ""}`}
        data-apply-field="applicant-name"
      >
        <Label htmlFor="ap-name">Applicant&rsquo;s full name</Label>
        <Input
          id="ap-name"
          autoComplete="name"
          placeholder="The prospective student's name"
          value={name}
          onChange={(e) => changeField(setName, e.target.value)}
          maxLength={200}
          required
        />
      </div>
      {showProgram && (
        <div className="grid gap-2" data-apply-field="program">
          <Label htmlFor="ap-program">Program</Label>
          <Select value={programId} onValueChange={(value) => changeField(setProgramId, value)}>
            <SelectTrigger id="ap-program" className="w-full">
              <SelectValue placeholder="Select a program" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNDECIDED}>Not sure yet — help me choose</SelectItem>
              {(programs.data ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div
        className={`grid gap-2 ${editorialSections ? "lg:col-span-2" : ""}`}
        data-apply-field="applicant-role"
      >
        <Label htmlFor="ap-applicant-is">{applicantRoleLabel}</Label>
        <Select
          value={applicantIs}
          onValueChange={(value) =>
            changeField(setApplicantIs, value as ApplyRequest["applicant_is"])
          }
        >
          <SelectTrigger id="ap-applicant-is" className="w-full">
            <SelectValue placeholder="Select one" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="parent">Parent / guardian</SelectItem>
            <SelectItem value="student">The student</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {intake.isPending && (
        <p
          className={`flex items-center gap-2 text-meta text-muted-foreground ${editorialSections ? "md:col-span-2 lg:col-span-6" : ""}`}
          data-apply-intake-pending
        >
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Loading the rest of the form…
        </p>
      )}
      <div
        className={`grid gap-2 ${editorialSections ? "md:col-span-2 lg:col-span-2" : ""}`}
        data-apply-field="phone"
      >
        <Label htmlFor="ap-phone" data-apply-qualified={phoneHint ? "" : undefined}>
          Phone
          {phoneHint && (
            <span className="text-muted-foreground" data-apply-qualifier>
              {" "}({phoneHint})
            </span>
          )}
        </Label>
        <Input
          id="ap-phone"
          type="tel"
          autoComplete="tel"
          placeholder={phoneRequired ? undefined : "Optional"}
          value={phone}
          onChange={(e) => changeField(setPhone, e.target.value)}
          maxLength={50}
          required={phoneRequired}
        />
      </div>
      <div
        className={`grid gap-2 ${editorialSections ? "lg:col-span-3" : ""}`}
        data-apply-field="email"
      >
        <Label htmlFor="ap-email">Email</Label>
        <Input
          id="ap-email"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="you@example.com"
          value={email}
          onChange={(e) => changeEmailField(setEmail, e.target.value)}
          aria-invalid={emailMismatch}
          aria-describedby={emailMismatch ? "ap-email-error" : undefined}
          maxLength={320}
          required
        />
      </div>
      <div
        className={`grid gap-2 ${editorialSections ? "lg:col-span-3" : ""}`}
        data-apply-field="confirm-email"
      >
        <Label htmlFor="ap-email-confirm">Confirm email</Label>
        <Input
          id="ap-email-confirm"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="Re-enter your email"
          value={confirmEmail}
          onChange={(e) => changeEmailField(setConfirmEmail, e.target.value)}
          onBlur={() => {
            if (confirmEmail.trim()) {
              setEmailMismatch(
                email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase(),
              );
            }
          }}
          aria-invalid={emailMismatch}
          aria-describedby={emailMismatch ? "ap-email-error" : "ap-email-confirm-hint"}
          maxLength={320}
          required
        />
        {emailMismatch ? (
          <p
            id="ap-email-error"
            role="alert"
            className="text-meta font-medium text-destructive"
          >
            Email addresses do not match. Check both entries.
          </p>
        ) : (
          <p
            id="ap-email-confirm-hint"
            className="text-meta text-muted-foreground"
            data-apply-confirm-hint
          >
            Enter it again so we can reach you about the {submissionNoun}.
          </p>
        )}
      </div>
    </>
  );

  return (
    <form
      onSubmit={onSubmit}
      className="grid gap-4"
      data-apply-editorial={editorialSections ? "" : undefined}
    >
      {editorialSections ? (
        <section
          className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6"
          aria-labelledby="ap-contact-section-heading"
          data-apply-section
          data-apply-contact-section
        >
          {renderSectionHeading(
            "Contact",
            "ap-contact-section-heading",
            "lg:col-span-6",
          )}
          {contactFields}
        </section>
      ) : (
        contactFields
      )}
      {participantFields.length > 0 && (
        // Placed after the contact fields, not interleaved with them: these
        // describe the student, while the email and phone above reach whoever
        // is applying. Mixing the two put "Major" beside "Email" in the
        // landing page's two-column layout.
        <section
          className={`grid gap-4 border-t border-rule pt-4 ${editorialSections ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : ""}`}
          aria-labelledby="ap-participant-section-heading"
          data-apply-section
          data-apply-participant-section
        >
          {renderSectionHeading(
            editorialSections ? "Participant" : PARTICIPANT_SECTION_HEADING,
            "ap-participant-section-heading",
            "lg:col-span-3",
          )}
          {renderDetailFields("participant")}
        </section>
      )}
      {guardianFields.length > 0 && (
        // A grid rather than space-y: one column here reads the same as the
        // stack it replaces, and a caller can re-column it without fighting
        // sibling margins.
        <section
          className={`grid gap-4 border-t border-rule pt-4 ${editorialSections ? "grid-cols-1 md:grid-cols-2" : ""}`}
          aria-labelledby="ap-guardian-section-heading"
          data-apply-section
          data-apply-guardian-section
        >
          {renderSectionHeading(
            editorialSections ? "Parent / guardian" : GUARDIAN_SECTION_HEADING,
            "ap-guardian-section-heading",
            "lg:col-span-2",
          )}
          {renderDetailFields("guardian")}
          {isUnderThirteen && (
            <div
              className="grid gap-2 lg:col-span-2"
              data-apply-field="guardian_consent"
              data-apply-wide=""
            >
              <label className="flex items-start gap-2 text-ui" htmlFor="ap-guardian-consent">
                <Checkbox
                  className="mt-0.5"
                  id="ap-guardian-consent"
                  checked={guardianConsent}
                  disabled={!guardianNamed}
                  onCheckedChange={(checked) => {
                    submissionId.current = null;
                    setGuardianConsent(checked === true);
                  }}
                />
                <span>
                  I am the parent or guardian named above, and I consent to this school
                  collecting and holding my child&rsquo;s information as described in its{" "}
                  <a className={`${PUBLIC_FOCUS} underline`} href="/privacy" target="_blank" rel="noreferrer">
                    privacy policy
                  </a>
                  .
                </span>
              </label>
              {!guardianNamed && (
                <p className="text-meta text-muted-foreground">
                  Name a parent or guardian above before confirming consent.
                </p>
              )}
            </div>
          )}
        </section>
      )}
      <div className="grid gap-2" data-apply-note>
        <Label htmlFor="ap-note">
          Anything we should know? <span className="text-muted-foreground">(optional)</span>
        </Label>
        <Textarea
          id="ap-note"
          rows={3}
          placeholder={notePlaceholder}
          value={note}
          onChange={(e) => changeField(setNote, e.target.value)}
          maxLength={2000}
        />
      </div>
      {intake.isError && (
        <Alert variant="destructive" data-apply-alert>
          <AlertDescription>
            <p>
              We couldn&rsquo;t load the {submissionNoun} form. Reload the page and try again.
              {contactEmail && (
                <>
                  {" "}Or email{" "}
                  <a href={`mailto:${contactEmail}`} className={`${PUBLIC_LINK} font-medium underline`}>
                    {contactEmail}
                  </a>
                  .
                </>
              )}
            </p>
          </AlertDescription>
        </Alert>
      )}
      {submitError && (
        <Alert variant="destructive" data-apply-alert>
          <AlertDescription>
            <p>
              {submitError}
              {contactEmail && (
                <>
                  {" "}If the {submissionNoun} is time-sensitive, email{" "}
                  <a href={`mailto:${contactEmail}`} className={`${PUBLIC_LINK} font-medium underline`}>
                    {contactEmail}
                  </a>
                  .
                </>
              )}
            </p>
          </AlertDescription>
        </Alert>
      )}
      <TurnstileField cycle={turnstileCycle} onTokenChange={setTurnstileToken} />
      <Button
        type="submit"
        className="w-full"
        disabled={
          submitting ||
          !applicantIs ||
          intakeUnsettled ||
          missingRequiredDetail ||
          (turnstileEnabled && !turnstileToken)
        }
      >
        {submitting && <Loader2 className="size-4 animate-spin" />}
        {submitLabel}
        {editorialSections && !submitting && (
          <ArrowRight className="size-5" strokeWidth={1.8} aria-hidden="true" />
        )}
      </Button>
      <p className="text-center text-small text-muted-foreground">
        By sending this {submissionNoun}, you allow the admissions team to use these
        details to contact you and place the student. Do not include medical,
        financial, or government identity documents. No account or payment is created.{" "}
        {/* The person handing over a minor's details and a guardian's phone
            number is exactly the person entitled to read these first. */}
        <Link to={LEGAL_ROUTES.privacy} className={`${PUBLIC_LINK} font-medium underline`}>
          Privacy policy
        </Link>
        {" · "}
        <Link to={LEGAL_ROUTES.terms} className={`${PUBLIC_LINK} font-medium underline`}>
          Terms of service
        </Link>
      </p>
    </form>
  );
}
